import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { BulkUpdateSelection } from '~/api/civilsearch';
import type { DataRequestV2 } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useBulkUpdateDataRequests } from '~/hooks/civilsearch/useDataRequest';

export type UseBulkEditAgencyNotesParams = {
  buildSearchPayloadForBulk: () => { [key: string]: any };
  getActiveQueryKeyParams: () => object;
};

export function useBulkEditAgencyNotes({
  buildSearchPayloadForBulk,
  getActiveQueryKeyParams,
}: UseBulkEditAgencyNotesParams) {
  const queryClient = useQueryClient();
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [notes, setNotes] = useState<string>('');
  const mutation = useBulkUpdateDataRequests();

  const open = useCallback((selection: ServerSideSelectionState) => {
    setPendingSelection(selection);
    setNotes('');
  }, []);

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const onSubmit = useCallback(() => {
    if (!pendingSelection) return;

    const selection: BulkUpdateSelection =
      pendingSelection.mode === 'all' ? { ...pendingSelection, search: buildSearchPayloadForBulk() } : pendingSelection;

    const activeQueryKey = ['data-requests-v2', getActiveQueryKeyParams()];

    mutation.mutate(
      { selection, updates: { agencyNotes: notes } },
      {
        onSuccess: () => {
          setPendingSelection(null);
          queryClient.setQueryData<{ requests: DataRequestV2[] }>(activeQueryKey, (old) => {
            if (!old) return old;
            return {
              ...old,
              requests: old.requests.map((row) => {
                const affected =
                  selection.mode === 'partial'
                    ? selection.selectedIds.includes(row.id)
                    : !selection.excludedIds.includes(row.id);
                if (!affected) return row;
                const existing = row.agency?.notes || '';
                return {
                  ...row,
                  agency: {
                    ...row.agency,
                    notes: existing ? `${existing}\n\n${notes}` : notes,
                  },
                };
              }),
            };
          });
        },
        onError: () => {
          Notify.error({ message: 'Failed to update agency notes. Please try again.' });
        },
      },
    );
  }, [pendingSelection, notes, buildSearchPayloadForBulk, getActiveQueryKeyParams, mutation, queryClient]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    notes,
    setNotes,
    onSubmit,
    isPending: mutation.isPending,
  };
}
